import { StandardEvents } from '@shopify/events';

const WAIT_MS = 5000;
let transitionActive = false;
let mutationFrame = 0;
let redirectTimeout = 0;
let countdownInterval = 0;
let previousFocus = null;

function cartUrl() {
  return document.querySelector('meta[name="rebela-cart-url"]')?.getAttribute('content') || '/cart';
}

function disableCartAutoOpen() {
  document.querySelectorAll('cart-drawer-component[auto-open]').forEach((drawer) => drawer.removeAttribute('auto-open'));
}

function suppressLegacyAdvancePopup(root = document) {
  const controls = root.querySelectorAll?.('button, a, [role="button"]') || [];
  controls.forEach((control) => {
    const controlText = (control.textContent || '').replace(/\s+/g, ' ').trim().toUpperCase();
    if (!controlText.includes('I UNDERSTAND')) return;

    let candidate = control.parentElement;
    while (candidate && candidate !== document.body && candidate !== document.documentElement) {
      const text = (candidate.textContent || '').replace(/\s+/g, ' ').trim().toUpperCase();
      if (text.includes('ADVANCE PAYMENT POLICY') && text.includes('I UNDERSTAND') && text.length < 2500) {
        if (candidate instanceof HTMLDialogElement && candidate.open) candidate.close();
        candidate.remove();
        break;
      }
      candidate = candidate.parentElement;
    }
  });
}

function decorateCart(root = document) {
  root.querySelectorAll?.('.cart__checkout-button .button-text').forEach((label) => {
    label.textContent = 'PROCEED TO CHECKOUT';
  });
}

function clearTransitionTimers() {
  window.clearTimeout(redirectTimeout);
  window.clearInterval(countdownInterval);
  redirectTimeout = 0;
  countdownInterval = 0;
}

function closeTransition() {
  const screen = document.querySelector('[data-rebela-payment-transition]');
  clearTransitionTimers();
  if (!screen) return;

  screen.classList.remove('is-active', 'is-counting');
  document.body.classList.remove('rh-payment-transition-active');
  screen.hidden = true;
  transitionActive = false;

  if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
}

function goToCart() {
  clearTransitionTimers();
  window.location.assign(cartUrl());
}

function trapFocus(event, screen) {
  if (event.key === 'Escape') {
    event.preventDefault();
    closeTransition();
    return;
  }

  if (event.key !== 'Tab') return;
  const focusable = Array.from(screen.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])')).filter((node) => !node.disabled && node.offsetParent !== null);
  if (!focusable.length) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function buildTransition() {
  let screen = document.querySelector('[data-rebela-payment-transition]');
  if (screen) return screen;

  screen = document.createElement('section');
  screen.className = 'rh-payment-transition';
  screen.hidden = true;
  screen.setAttribute('data-rebela-payment-transition', '');
  screen.setAttribute('role', 'dialog');
  screen.setAttribute('aria-modal', 'true');
  screen.setAttribute('aria-labelledby', 'RhCartTransitionTitle');
  screen.setAttribute('aria-describedby', 'RhCartTransitionCopy');
  screen.innerHTML = `
    <div class="rh-payment-transition__top" aria-hidden="true">
      <span>REBELA</span>
      <span>Item added</span>
    </div>
    <div class="rh-payment-transition__inner">
      <p class="rh-payment-transition__eyebrow">YOUR CART</p>
      <h2 id="RhCartTransitionTitle">Item added to your cart.</h2>
      <p class="rh-payment-transition__copy" id="RhCartTransitionCopy">
        Review your item and order details in the cart before checkout.
      </p>
      <div class="rh-payment-transition__actions">
        <button type="button" data-rh-payment-cart>View cart now</button>
        <button type="button" data-rh-payment-close>Continue shopping</button>
      </div>
    </div>
    <div class="rh-payment-transition__bottom">
      <div class="rh-payment-transition__redirect">
        <span>Continuing to your cart</span>
        <small aria-live="polite">Automatic redirect in <b data-rh-payment-seconds>5</b> seconds</small>
      </div>
      <strong class="rh-payment-transition__count" data-rh-payment-count aria-hidden="true">05</strong>
      <div class="rh-payment-transition__progress" aria-hidden="true"><span></span></div>
    </div>
  `;

  screen.addEventListener('keydown', (event) => trapFocus(event, screen));
  screen.querySelector('[data-rh-payment-cart]')?.addEventListener('click', goToCart);
  screen.querySelector('[data-rh-payment-close]')?.addEventListener('click', closeTransition);
  document.body.appendChild(screen);
  return screen;
}

function showCartTransition() {
  if (transitionActive) return;
  transitionActive = true;
  previousFocus = document.activeElement;

  suppressLegacyAdvancePopup();
  const screen = buildTransition();
  const secondsText = screen.querySelector('[data-rh-payment-seconds]');
  const countText = screen.querySelector('[data-rh-payment-count]');
  const primaryAction = screen.querySelector('[data-rh-payment-cart]');
  const started = Date.now();

  screen.hidden = false;
  document.body.classList.add('rh-payment-transition-active');
  requestAnimationFrame(() => {
    screen.classList.add('is-active');
    requestAnimationFrame(() => {
      screen.classList.add('is-counting');
      primaryAction?.focus();
    });
  });

  const update = () => {
    const elapsed = Date.now() - started;
    const remaining = Math.max(0, Math.ceil((WAIT_MS - elapsed) / 1000));
    if (secondsText) secondsText.textContent = String(remaining);
    if (countText) countText.textContent = String(remaining).padStart(2, '0');
  };
  update();

  countdownInterval = window.setInterval(update, 200);
  redirectTimeout = window.setTimeout(goToCart, WAIT_MS);
}

function maintainExperience() {
  disableCartAutoOpen();
  suppressLegacyAdvancePopup();
  decorateCart();
}

maintainExperience();

const observer = new MutationObserver(() => {
  if (mutationFrame) return;
  mutationFrame = requestAnimationFrame(() => {
    mutationFrame = 0;
    maintainExperience();
  });
});
observer.observe(document.body, { childList: true, subtree: true });

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action !== 'add' || !event.promise) return;

  event.promise
    .then(({ detail }) => {
      if (detail?.didError) return;
      showCartTransition();
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[rebela-cart-flow] Cart add promise rejected:', error);
    });
});
